import { useTranslation } from 'react-i18next'
import * as Switch from '@radix-ui/react-switch'

import type { PushCatalog, PushCategory, PushCategoryFilter, PushFilters } from '@/types'
import { cn } from '@/lib/utils'

const ORDER: PushCategory[] = ['animal', 'human', 'vehicle', 'audio', 'other']

/**
 * `other` cannot be narrowed to individual classes. It covers motion-only captures, which
 * carry NO detected classes at all, so narrowing it would replace the category token with a
 * class list and motion — having no class — would then match nothing. Same rule as the
 * per-account picker in PushCategoriesSection.
 */
const NOT_NARROWABLE: ReadonlySet<PushCategory> = new Set<PushCategory>(['other'])

/** The unidentified-animal fallback box. Rendered first, and never as the bare word. */
const FALLBACK_ANIMAL = 'animal'

/** An absent category means enabled + unrestricted — `{}` is "everything is pushed". */
const DEFAULT_STATE: PushCategoryFilter = { enabled: true, classes: [] }

export interface PushCategoryPickerProps {
  filters: PushFilters
  catalog: PushCatalog
  onChange: (next: PushFilters) => void
  disabled?: boolean
}

/**
 * CP 159 — the per-camera alert category picker.
 *
 * Deliberately CONTROLLED and presentational: it fetches nothing and saves nothing, so the
 * camera sheet and (CP 162) the group bulk-apply dialog can drive the same UI from
 * different sources.
 *
 * The one non-obvious rule it encodes is the all-vs-specific choice. "Every box ticked" and
 * "all of this category" are NOT the same thing — the first freezes the selection to today's
 * species list, the second keeps including species a superuser adds later. Leaving that
 * implicit is how a filter silently stops matching a newly added species, so it is an
 * explicit radio rather than an inferred state.
 */
export function PushCategoryPicker({
  filters,
  catalog,
  onChange,
  disabled = false,
}: PushCategoryPickerProps) {
  const { t } = useTranslation('settings')

  const stateFor = (cat: PushCategory): PushCategoryFilter =>
    (filters as Partial<PushFilters>)?.[cat] ?? DEFAULT_STATE

  const classesFor = (cat: PushCategory): string[] => {
    const names = catalog.groups.find((g) => g.category === cat)?.class_names ?? []
    if (cat !== 'animal') return names
    // The fallback first, so "Unidentified animal" is not buried among species.
    return [...names].sort((a, b) => {
      if (a === FALLBACK_ANIMAL) return -1
      if (b === FALLBACK_ANIMAL) return 1
      return a.localeCompare(b)
    })
  }

  const classLabel = (cls: string) =>
    cls === FALLBACK_ANIMAL ? t('pushCategories.unidentifiedAnimal') : cls

  const patch = (cat: PushCategory, next: PushCategoryFilter) =>
    onChange({ ...(filters as Partial<PushFilters>), [cat]: next } as PushFilters)

  const toggleCategory = (cat: PushCategory) => {
    const current = stateFor(cat)
    patch(cat, { ...current, enabled: !current.enabled })
  }

  const setMode = (cat: PushCategory, mode: 'all' | 'some') => {
    const current = stateFor(cat)
    if (mode === 'all') {
      patch(cat, { ...current, classes: [] })
    } else {
      // Entering "only these" with nothing chosen would mean an empty list, which reads
      // back as "all" — seed with the full catalog so the choice is visible and truthful.
      patch(cat, { ...current, classes: current.classes.length ? current.classes : classesFor(cat) })
    }
  }

  const toggleClass = (cat: PushCategory, cls: string) => {
    const current = stateFor(cat)
    const next = current.classes.includes(cls)
      ? current.classes.filter((c) => c !== cls)
      : [...current.classes, cls]
    patch(cat, { ...current, classes: next })
  }

  // ── Warnings ───────────────────────────────────────────────────────────────
  // CP 163 removed the residual warning: 'other' no longer doubles as a catch-all for
  // vehicle and sound events, so muting a category alone now simply works.

  // A superuser can delete a species from the catalog; a saved filter can outlive it.
  const stale = ORDER.flatMap((cat) => {
    const known = new Set(classesFor(cat))
    return stateFor(cat).classes.filter((c) => !known.has(c))
  })

  return (
    <div className="space-y-3">
      {stale.length > 0 && (
        <p
          role="alert"
          className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400"
        >
          {t('pushCategories.staleClasses', { classes: stale.join(', ') })}
        </p>
      )}

      <div className="divide-y divide-border rounded-lg border border-border">
        {ORDER.map((cat) => {
          const state = stateFor(cat)
          const classes = classesFor(cat)
          const label = t(`pushCategories.categories.${cat}`)
          const switchId = `camera-push-category-${cat}`
          const narrowable = classes.length > 0 && !NOT_NARROWABLE.has(cat)
          const mode: 'all' | 'some' = state.classes.length === 0 ? 'all' : 'some'

          return (
            <div key={cat} className="p-3">
              <div className="flex items-center gap-3">
                <Switch.Root
                  id={switchId}
                  checked={state.enabled}
                  onCheckedChange={() => toggleCategory(cat)}
                  disabled={disabled}
                  aria-label={label}
                  className={cn(
                    'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    'data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-muted-foreground/30',
                  )}
                >
                  <Switch.Thumb className="pointer-events-none block h-3.5 w-3.5 rounded-full bg-white shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0.5" />
                </Switch.Root>

                <label htmlFor={switchId} className="min-w-0 flex-1 truncate text-sm">
                  {label}
                </label>
              </div>

              {narrowable && (
                <div className={cn('mt-3 pl-12', !state.enabled && 'opacity-50')}>
                  <div className="mb-2 flex flex-wrap gap-4">
                    <label
                      htmlFor={`${switchId}-all`}
                      className="flex items-center gap-2 text-xs"
                    >
                      <input
                        id={`${switchId}-all`}
                        type="radio"
                        name={`${switchId}-mode`}
                        checked={mode === 'all'}
                        onChange={() => setMode(cat, 'all')}
                        disabled={disabled || !state.enabled}
                        className="h-3.5 w-3.5 shrink-0"
                      />
                      <span>{t('pushCategories.allOf', { category: label })}</span>
                    </label>

                    <label
                      htmlFor={`${switchId}-some`}
                      className="flex items-center gap-2 text-xs"
                    >
                      <input
                        id={`${switchId}-some`}
                        type="radio"
                        name={`${switchId}-mode`}
                        checked={mode === 'some'}
                        onChange={() => setMode(cat, 'some')}
                        disabled={disabled || !state.enabled}
                        className="h-3.5 w-3.5 shrink-0"
                      />
                      <span>{t('pushCategories.onlyThese')}</span>
                    </label>
                  </div>

                  {mode === 'some' && (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {classes.map((cls) => (
                        <label key={cls} className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={state.classes.includes(cls)}
                            onChange={() => toggleClass(cat, cls)}
                            disabled={disabled || !state.enabled}
                            className="h-3.5 w-3.5 shrink-0 rounded border-border"
                          />
                          <span className="truncate" title={cls}>
                            {classLabel(cls)}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}

                  {cat === 'animal' && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {t('pushCategories.unidentifiedHint')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
